import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useCurrentUser } from "../../api/hooks";
import { useCompanySettings } from "../../api/hooks/defs/company";

declare global {
	interface Window {
		Beacon: (method: string, ...args: unknown[]) => void;
		_MyUser?: { name?: string; id?: string; email?: string };
	}
}

const HelpScoutBeacon = (): null => {
	const identified = useRef(false);
	const beaconReady = useRef(false);
	const { data: user } = useCurrentUser();
	const { data: company } = useCompanySettings();
	const location = useLocation();

	const identifyUser = useCallback(() => {
		if (!window.Beacon || !beaconReady.current) return;
		if (!user?.me || !company?.company) return;
		if (identified.current) return;

		identified.current = true;

		// user.me.aliases contains company aliases, not the user's name.
		// The actual user name is available from the server-rendered window._MyUser object.
		const userName = window._MyUser?.name ?? "";
		const userEmail = user.me.email;
		const userId = user.me.authorization?.payload?.user_id ?? "";
		const userRole = user.me.authorization?.payload?.user_primary_role ?? "";
		const companyId = company.company.id;
		const companyName = company.company.name;

		window.Beacon("identify", {
			name: userName,
			email: userEmail,
			company: companyName,
			"User-ID": userId,
			"User-Role": userRole,
			"Company-ID": companyId,
		});
	}, [user, company]);

	const identifyUserRef = useRef(identifyUser);
	identifyUserRef.current = identifyUser;

	// Register the Beacon ready listener once and clean up on unmount
	useEffect(() => {
		if (!window.Beacon) return;

		const onReady = (): void => {
			beaconReady.current = true;
			identifyUserRef.current();
		};

		const fixBeaconOnOpen = (): void => {
			const container = document.getElementById("beacon-container");
			if (!container) return;

			// Align the close button's x icon — the global `* { position: relative }`
			// shifts it from its intended offset.
			const icon = container.querySelector<HTMLElement>(".c-BeaconCloseButton__iconClose");
			if (icon) {
				icon.style.top = "1px";
			}

			const frame = container.querySelector("iframe");
			if (frame) {
				frame.removeAttribute("title");
			}
		};

		window.Beacon("on", "ready", onReady);
		window.Beacon("on", "open", fixBeaconOnOpen);

		return () => {
			window.Beacon("off", "ready", onReady);
			window.Beacon("off", "open", fixBeaconOnOpen);
			window.Beacon("logout");
			identified.current = false;
			beaconReady.current = false;
		};
	}, []);

	// Identify the user once both Beacon and user/company data are available
	useEffect(() => {
		identifyUser();
	}, [identifyUser]);

	// Notify Beacon of SPA route changes for page-specific suggestions.
	// Use a short delay so the document title has time to update after navigation.
	useEffect(() => {
		if (!window.Beacon) return;

		const timeout = setTimeout(() => {
			const url = window.location.href;
			const title = document.title || "PerformYard";

			if (url) {
				window.Beacon("event", {
					type: "page-viewed",
					url,
					title,
				});
			}
		}, 100);

		return () => clearTimeout(timeout);
	}, [location.pathname]);

	return null;
};

export default HelpScoutBeacon;
